import { BoxHeroHeadingContext } from "../../../domain/boxHeroHeading.js";
import { cn } from "../../../foundations/cn.js";
import { useLayout } from "../../../foundations/themeContext.js";
import { Stage } from "../../../moments/Stage.js";
import { Section } from "../../../patterns/Section.js";
import type { PageLayoutProps } from "../../contract.js";
import { useChromeHints } from "../../shared/chromeHints.js";
import type {
  BoxPageBreadcrumbContent,
  BoxPageContent,
  BoxPageContentsContent,
  BoxPageHeadingContent,
} from "../boxPageContent.js";
import { BoxPageBreadcrumb } from "../sections/breadcrumb/BoxPageBreadcrumb.js";
import { BoxPageContents } from "../sections/contents/BoxPageContents.js";
import { BoxPageHeading } from "../sections/heading/BoxPageHeading.js";

/** The breadcrumb at the start of a centered column. */
export function StackedBreadcrumb(breadcrumb: BoxPageBreadcrumbContent) {
  return <BoxPageBreadcrumb {...breadcrumb} className="self-start" />;
}

/** The name and price centered under the stage. */
export function StackedHeading(heading: BoxPageHeadingContent) {
  return <BoxPageHeading {...heading} align="center" />;
}

/** The contents full width: the odds as a table by tier, each tier its shape, six cards a row. */
export function StackedContents(contents: BoxPageContentsContent) {
  return <BoxPageContents {...contents} oddsView="table" marker="shape" columns={6} />;
}

/**
 * A box's page, stacked: the box's name and its opener centered on a full-bleed stage that runs
 * under the nav, then everything inside at full width. Suits a short contents list, or a box that
 * is the show. The heading stays above the opener (`BoxHeroHeadingContext` off), so the opener's
 * hero leaves the name out.
 */
export function BoxPageStackedLayout({ sections }: PageLayoutProps<BoxPageContent>) {
  useChromeHints({ underNav: true });
  const layout = useLayout();
  return (
    <BoxHeroHeadingContext.Provider value={false}>
      <div className="relative isolate">
        {/* The stage runs under the nav, full bleed, like Home's hero. */}
        <div aria-hidden className="absolute inset-x-0 top-0 -z-10 h-[520px] md:h-[720px]">
          <Stage />
        </div>
        <Section
          className={cn(
            "flex flex-col items-center gap-6 pt-20 pb-10 text-center md:pb-16",
            layout.header === "floating" ? "md:pt-28" : "md:pt-6",
          )}
        >
          {sections.breadcrumb}
          {sections.heading}
          <div className="w-full max-w-[38rem] text-start">{sections.opener}</div>
        </Section>
      </div>
      <Section className="pb-20">{sections.contents}</Section>
    </BoxHeroHeadingContext.Provider>
  );
}
